import { z } from "zod";
import { historicalCatalogueReferenceSchema } from "./catalogue-management.js";
import { observationProtocolSchema } from "./observation-protocol.js";
import {
  atomicRequirementRelationSchema,
  learningResourceSchema,
  passportCatalogueSchema,
  passportCriterionSchema,
  passportPageSchema,
  passportStableIdSchema,
  stampAssessmentSchema,
  stampApplicabilitySchema,
  stampPathwayRelationSchema,
  stampSchema,
} from "./passport-catalogue.js";
import { employeeStampProgressSchema, stampEvidenceSchema } from "./passport-progress.js";
import { skillStatusSchema } from "./progress.js";

export const passportPageSummarySchema = z
  .object({
    page: passportPageSchema,
    required: z.number().int().nonnegative(),
    confirmed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    optional: z.number().int().nonnegative(),
    outOfScope: z.number().int().nonnegative(),
    taskTotal: z.number().int().nonnegative(),
    taskCompleted: z.number().int().nonnegative(),
    progressPercent: z.number().min(0).max(100),
    complete: z.boolean(),
    mandatoryGapStampIds: z.array(passportStableIdSchema),
  })
  .strict();
export const passportNextActionSchema = z
  .object({
    kind: z.enum(["assessment", "learning", "practice_evidence", "submit", "waiting", "complete"]),
    pageId: passportStableIdSchema,
    stampId: passportStableIdSchema,
    title: z.string().min(1),
    description: z.string().min(1),
  })
  .strict();
export const employeePassportOverviewSchema = z
  .object({
    employeeId: z.string().min(1),
    roleId: passportStableIdSchema,
    pages: z.array(passportPageSummarySchema),
    progress: z.array(employeeStampProgressSchema),
    nextAction: passportNextActionSchema.nullable(),
    confirmed: z.number().int().nonnegative(),
    required: z.number().int().nonnegative(),
    progressPercent: z.number().min(0).max(100),
  })
  .strict();
export const passportPageDetailSchema = z
  .object({
    summary: passportPageSummarySchema,
    stamps: z.array(
      z
        .object({
          stamp: stampSchema,
          applicability: stampApplicabilitySchema,
          progress: employeeStampProgressSchema.nullable(),
          prerequisitesMet: z.boolean(),
          unmetPrerequisiteStampIds: z.array(passportStableIdSchema),
        })
        .strict(),
    ),
  })
  .strict();
export const stampDetailSchema = z
  .object({
    stamp: stampSchema,
    page: passportPageSchema,
    criteria: z.array(passportCriterionSchema),
    learningResources: z.array(learningResourceSchema),
    relations: z.array(stampPathwayRelationSchema),
    atomicRelations: z.array(atomicRequirementRelationSchema),
    assessment: stampAssessmentSchema.nullable(),
    progress: employeeStampProgressSchema,
    evidence: z.array(stampEvidenceSchema),
    recommendedResourceIds: z.array(passportStableIdSchema),
    unmetGates: z.array(z.enum(["knowledge", "practice", "behaviour", "prerequisite"])),
  })
  .strict();
export const legacySkillHistorySchema = z
  .object({
    employeeId: z.string().min(1),
    skillId: z.string().min(1),
    skillTitle: z.string().min(1),
    status: skillStatusSchema,
    label: z.literal("legacy_prototype_evidence"),
    catalogue: historicalCatalogueReferenceSchema.optional(),
  })
  .strict();
export const stampChecklistItemSchema = z
  .object({
    id: z.string().min(1),
    employeeId: z.string().min(1),
    employeeName: z.string().min(1),
    roleTitle: z.string().min(1),
    page: passportPageSchema,
    stamp: stampSchema,
    progress: employeeStampProgressSchema,
    evidence: z.array(stampEvidenceSchema),
    criteria: z.array(passportCriterionSchema),
    observationProtocol: observationProtocolSchema.nullable(),
    submittedAt: z.string().datetime(),
  })
  .strict();
export const passportReadinessStampDetailSchema = z
  .object({
    progress: employeeStampProgressSchema,
    nextAction: passportNextActionSchema.shape.kind,
    recentActivity: z.array(
      z
        .object({
          summary: z.string().min(1),
          timestamp: z.string().datetime(),
        })
        .strict(),
    ),
  })
  .strict();
export const passportReadinessEmployeeSchema = z
  .object({
    employeeId: z.string().min(1),
    employeeName: z.string().min(1),
    complete: z.boolean(),
    statuses: z.record(passportStableIdSchema, skillStatusSchema),
    details: z.record(passportStableIdSchema, passportReadinessStampDetailSchema),
  })
  .strict();
export const passportReadinessPageSchema = z
  .object({
    page: passportPageSchema,
    requiredStampIds: z.array(passportStableIdSchema),
    confirmed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    toTrain: z.number().int().nonnegative(),
    unassessed: z.number().int().nonnegative(),
    gaps: z.number().int().nonnegative(),
    rows: z.array(passportReadinessEmployeeSchema),
  })
  .strict();
export const passportRoleReadinessSchema = z
  .object({
    roleId: passportStableIdSchema,
    pages: z.array(passportReadinessPageSchema),
    deployableEmployeeIds: z.array(z.string().min(1)),
  })
  .strict();
export const passportRoleMatchSchema = z
  .object({
    employeeId: z.string().min(1),
    employeeName: z.string().min(1),
    confirmedRequired: z.number().int().nonnegative(),
    pendingRequired: z.number().int().nonnegative(),
    gapStampIds: z.array(passportStableIdSchema),
    gapStatuses: z.record(passportStableIdSchema, skillStatusSchema),
    incompletePageIds: z.array(passportStableIdSchema),
    deployable: z.boolean(),
  })
  .strict();
export const passportActivityEventSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1),
    actorId: z.string().min(1),
    employeeId: z.string().min(1),
    pageId: passportStableIdSchema,
    stampId: passportStableIdSchema,
    timestamp: z.string().datetime(),
    resultingStatus: skillStatusSchema.nullable(),
    feedback: z.string().nullable(),
    summary: z.string().min(1),
    catalogue: historicalCatalogueReferenceSchema,
  })
  .strict();
export const passportBootstrapSchema = z
  .object({
    catalogue: passportCatalogueSchema,
    employeeOverview: employeePassportOverviewSchema.nullable(),
    checklist: z.array(stampChecklistItemSchema),
    readiness: z.array(passportRoleReadinessSchema),
    roleMatches: z.record(passportStableIdSchema, z.array(passportRoleMatchSchema)),
    events: z.array(passportActivityEventSchema),
    legacyHistory: z.array(legacySkillHistorySchema),
  })
  .strict();

export type PassportPageSummary = z.infer<typeof passportPageSummarySchema>;
export type PassportNextAction = z.infer<typeof passportNextActionSchema>;
export type EmployeePassportOverview = z.infer<typeof employeePassportOverviewSchema>;
export type PassportPageDetail = z.infer<typeof passportPageDetailSchema>;
export type StampDetail = z.infer<typeof stampDetailSchema>;
export type LegacySkillHistory = z.infer<typeof legacySkillHistorySchema>;
export type StampChecklistItem = z.infer<typeof stampChecklistItemSchema>;
export type PassportRoleReadiness = z.infer<typeof passportRoleReadinessSchema>;
export type PassportRoleMatch = z.infer<typeof passportRoleMatchSchema>;
export type PassportActivityEvent = z.infer<typeof passportActivityEventSchema>;
export type PassportBootstrap = z.infer<typeof passportBootstrapSchema>;
